import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, useTokens } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio } from '../utils/luminance';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ResidentAccess() {
  const portalUrl = usePropertyField('property_resident_portal_url') as string || '';
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string || '';
  const defaultPhone = usePropertyField('property_phone') as string || '';
  const phone = callTrackingPhone || defaultPhone;
  const helpCtaLabel = useContentToken('residentaccess.help_cta_label');

  const heading = useContentToken('residentaccess.heading');
  const portalTitle = useContentToken('residentaccess.portal_title');
  const portalCta = useContentToken('residentaccess.portal_cta_label');
  const helpTitle = useContentToken('residentaccess.help_title');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  // Match the canonical "CTA over white" treatment used elsewhere in the
  // theme (PropertyOverview): outline button that fills with the accent on
  // hover. When the property's primary fails WCAG AA contrast against the
  // white card background, fall back to the dark text token so the CTA
  // stays legible — same luminance picker the overview/header CTAs use.
  const tokens = useTokens();
  const [ctaAccent, ctaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  return (
    <section
      className="ra-section"
      style={{
        ['--rise-ra-cta-accent' as string]: ctaAccent,
        ['--rise-ra-cta-on-accent' as string]: ctaOnAccent,
      }}
    >
      <div ref={sectionRef} className="reveal-fade-up ra-container">
        <EditableToken path="residentaccess.heading" as="h2" className="sr-only">
          {heading || 'Resident Access'}
        </EditableToken>
        <div className="ra-grid">
          {/* Resident Portal Card */}
          <div className="ra-card">
            <div className="ra-card-icon-wrap ra-card-icon--portal">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="residentaccess.portal_title" as="h3" className="ra-card-title">
              {portalTitle || 'Resident Portal'}
            </EditableToken>
            <RichTextContent path="residentaccess.portal_description" as="p" className="ra-card-desc" />
            {portalUrl ? (
              <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="ra-btn">
                <span className="ra-btn-label">
                  <EditableToken path="residentaccess.portal_cta_label" as="span">
                    {portalCta || 'Access Portal Login'}
                  </EditableToken>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </a>
            ) : (
              <span className="ra-btn ra-btn--disabled">
                <span className="ra-btn-label">Portal URL Not Available</span>
              </span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ra-card">
            <div className="ra-card-icon-wrap ra-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="residentaccess.help_title" as="h3" className="ra-card-title">
              {helpTitle || 'Need Help? Call Us'}
            </EditableToken>
            <RichTextContent path="residentaccess.help_description" as="p" className="ra-card-desc" />
            <a
              href={phone ? `tel:${phone}` : '#'}
              className="dniphone dniphonehref ra-btn"
            >
              <span className="ra-btn-label">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                <EditableToken path="residentaccess.help_cta_label" as="span">
                  {formatPhone(helpCtaLabel || phone)}
                </EditableToken>
              </span>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ra-section { padding: 2.5rem 0 4rem; }
        }
        .ra-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ra-container { padding: 0 1.5rem; } }

        .ra-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ra-grid { grid-template-columns: 1fr 1fr; }
        }

        .ra-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .ra-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .ra-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .ra-card-icon--portal {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }
        .ra-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .ra-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .ra-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        /* Outline CTA matching the canonical "over white" treatment used in
           PropertyOverview / PetPolicy: transparent body, accent border +
           label, fills with the accent on hover. The accent is luminance-
           picked (--rise-ra-cta-accent) so pale primaries fall back to text. */
        .ra-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          border-radius: 9999px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--rise-ra-cta-accent, var(--vibe-color-primary));
          color: var(--rise-ra-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
          transition: color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          margin-top: auto;
        }
        .ra-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--rise-ra-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .ra-btn-label {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .ra-btn:hover {
          color: var(--rise-ra-cta-on-accent, var(--vibe-color-text-on-primary));
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--rise-ra-cta-accent, var(--vibe-color-primary)) 30%, transparent);
        }
        .ra-btn:hover::before { opacity: 1; }
        .ra-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .ra-btn--disabled,
        .ra-btn--disabled:hover {
          background: transparent;
          color: var(--vibe-color-text-muted);
          border-color: var(--vibe-color-border, #e5e7eb);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }
        .ra-btn--disabled::before { display: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Resident Access',
    },
    'residentaccess.portal_title': {
      label: 'Portal Card Title',
      type: 'text',
      defaultValue: 'Resident Portal',
    },
    'residentaccess.portal_description': {
      label: 'Portal Card Description',
      type: 'richtext',
      defaultValue:
        '<p>With convenient 24/7 access, the resident portal makes it easy to request maintenance service, pay your rent online, and manage your account. Log in to get started.</p>',
    },
    'residentaccess.portal_cta_label': {
      label: 'Portal CTA Label',
      type: 'text',
      defaultValue: 'Access Portal Login',
    },
    'residentaccess.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Need Help? Call Us',
    },
    'residentaccess.help_description': {
      label: 'Help Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Our team is here to help with any questions about your lease, maintenance requests, or community guidelines. Don\'t hesitate to reach out.</p>',
    },
    'residentaccess.help_cta_label': {
      label: 'Phone CTA Label',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
  },
};

(ResidentAccess as any).contentSchema = contentSchema;

export default ResidentAccess;
